import SlidingPanel from "@components/sliders/SlidingPanel";
import Switcher from "@components/switcher/Switcher";
import { userManagementApis } from "@services/apis/UserManagement";
import SettingTypes, {
  GetSecuritySettingsResponse,
} from "@services/models/UserManagement/get-security-settings";
import React, { useEffect, useState } from "react";
import SecurityAccount from "./AccountSecurity";
import SecurityGeneral from "./GeneralSecurity";

interface SecuritySettingsSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (success: boolean) => void;
  userId: number;
}

const SecuritySettingsSlider: React.FC<SecuritySettingsSliderProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userId,
}) => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<GetSecuritySettingsResponse | null>(
    null,
  );
  const [is2FaEnabled, setIs2FaEnabled] = useState<boolean>(false);
  const [emailOtpRequired, setEmailOtpRequired] = useState<boolean>(false);
  const [google2FARequired, setGoogle2FaRequired] = useState<boolean>(false);

  const fetchSecuritySettings = async () => {
    try {
      setLoading(true);
      const response = await userManagementApis.getSecuritySettings(userId);
      setSettings(response);
      setIs2FaEnabled(response.data[0].settingValue as boolean);
      const changeSecuritySettings = response.data.find(
        (setting) => setting.frontEndKey === "changeSecuritySettings",
      );
      if (
        changeSecuritySettings &&
        typeof changeSecuritySettings.settingValue === "object" &&
        "email" in changeSecuritySettings.settingValue
      ) {
        setEmailOtpRequired(changeSecuritySettings.settingValue.email.value);
        setGoogle2FaRequired(
          changeSecuritySettings.settingValue.google2FA.value,
        );
      }

      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchSecuritySettings();
  }, [isOpen, userId]);

  const tabs = [
    {
      label: SettingTypes.account.toUpperCase(),
      component: (
        <SecurityAccount
          userId={userId}
          settingsData={settings?.data.filter(
            (setting) =>
              setting.settingType === SettingTypes.account &&
              setting.frontEndKey !== "is2FAEnabled",
          )}
          loading={loading}
          setAccountSettings={setSettings}
          is2FaEnabled={is2FaEnabled}
          setEmailOtpRequired={setEmailOtpRequired}
          setGoogle2FaRequired={setGoogle2FaRequired}
        />
      ),
    },
    {
      label: SettingTypes.general.toUpperCase(),
      component: (
        <SecurityGeneral
          userId={userId}
          settingsData={settings?.data.filter(
            (setting) =>
              setting.settingType === SettingTypes.general &&
              setting.frontEndKey !== "is2FAEnabled",
          )}
          loading={loading}
          setGeneralSettings={setSettings}
          is2FaEnabled={is2FaEnabled}
          emailOtpRequired={emailOtpRequired}
          google2FaRequired={google2FARequired}
        />
      ),
    },
  ];

  if (error) {
    return (
      <SlidingPanel isOpen={isOpen} onClose={onClose}>
        <div className="p-4 text-red-500">{error}</div>
      </SlidingPanel>
    );
  }

  return (
    <div className="flex flex-col px-[16px] bg-bg-base">
      <div className="-mx-[16px]">
        <Switcher tabs={tabs} />
      </div>
    </div>
  );
};

export default SecuritySettingsSlider;
